import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ImagePlus, Images } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  is_allowed_website_media_image_url,
  WEBSITE_MEDIA_DEFINITIONS_BY_KEY,
  WEBSITE_MEDIA_PAGE_ORDER,
  WEBSITE_MEDIA_PLACEMENTS,
  WebsiteMediaEntry,
  WebsiteMediaPlacementKey,
  website_media_page,
} from '@pigeons/shared-website-media';
import { resolve_website_asset_url } from '../lib/site_config';
import { websiteMediaService } from '../services/websiteMediaService';
import WebsiteImageLibraryModal from '../components/admin/WebsiteImageLibraryModal';

/** Pages the admin can manage photos for. Any other placement page (global social share,
 * the resource/pricing fallback images) still works on the live site off its registry
 * default — it's just no longer surfaced here for editing. */
const ADMIN_VISIBLE_PAGES: readonly website_media_page[] = ['home', 'about', 'services'];

type singleton_draft = {
  image_url: string;
  alt_text_fr: string;
  alt_text_en: string;
};

type singleton_draft_map = Record<WebsiteMediaPlacementKey, singleton_draft>;

const get_error_message = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const build_singleton_drafts = (
  entriesByKey: Partial<Record<WebsiteMediaPlacementKey, WebsiteMediaEntry>>
): singleton_draft_map => {
  return WEBSITE_MEDIA_PLACEMENTS.reduce((accumulator, definition) => {
    const entry = entriesByKey[definition.key];
    accumulator[definition.key] = {
      image_url: entry?.image_url || definition.default_image_url,
      alt_text_fr: entry?.alt_text_fr || definition.default_alt_text_fr,
      alt_text_en: entry?.alt_text_en || definition.default_alt_text_en,
    };
    return accumulator;
  }, {} as singleton_draft_map);
};

const build_entries_map = (entries: WebsiteMediaEntry[]) =>
  entries.reduce<Partial<Record<WebsiteMediaPlacementKey, WebsiteMediaEntry>>>(
    (accumulator, entry) => {
      accumulator[entry.placement_key] = entry;
      return accumulator;
    },
    {}
  );

export default function WebsiteContentAdmin() {
  const { t, i18n } = useTranslation('admin');
  const currentLanguage = i18n.language.startsWith('fr') ? 'fr' : 'en';

  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [selectedPage, setSelectedPage] = useState<website_media_page | null>(null);

  const [singletonEntriesByKey, setSingletonEntriesByKey] = useState<
    Partial<Record<WebsiteMediaPlacementKey, WebsiteMediaEntry>>
  >({});
  const [singletonDrafts, setSingletonDrafts] = useState<singleton_draft_map>(() =>
    build_singleton_drafts({})
  );

  const [websiteImageLibraryOpen, setWebsiteImageLibraryOpen] = useState(false);
  const [websiteImageLibraryTarget, setWebsiteImageLibraryTarget] =
    useState<WebsiteMediaPlacementKey | null>(null);

  const groupedSingletonPlacements = useMemo(() => {
    return WEBSITE_MEDIA_PAGE_ORDER.filter(page => ADMIN_VISIBLE_PAGES.includes(page)).map(
      page => ({
        page,
        sections: Array.from(
          WEBSITE_MEDIA_PLACEMENTS.filter(definition => definition.page === page).reduce(
            (accumulator, definition) => {
              const current = accumulator.get(definition.section) ?? [];
              current.push(definition);
              accumulator.set(definition.section, current);
              return accumulator;
            },
            new Map<string, (typeof WEBSITE_MEDIA_PLACEMENTS)[number][]>()
          )
        ),
      })
    );
  }, []);

  const setSaving = (token: string, value: boolean) => {
    setSavingKeys(prev => ({
      ...prev,
      [token]: value,
    }));
  };

  const refreshContent = async () => {
    setLoading(true);
    try {
      const singletonEntries = await websiteMediaService.listWebsiteMedia();
      const entriesMap = build_entries_map(singletonEntries);
      setSingletonEntriesByKey(entriesMap);
      setSingletonDrafts(build_singleton_drafts(entriesMap));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshContent();
  }, []);

  const updateSingletonDraft = (
    placementKey: WebsiteMediaPlacementKey,
    field: keyof singleton_draft,
    value: string
  ) => {
    setSingletonDrafts(prev => ({
      ...prev,
      [placementKey]: {
        ...prev[placementKey],
        [field]: value,
      },
    }));
  };

  const handleSingletonImageUpload = async (
    placementKey: WebsiteMediaPlacementKey,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const token = `singleton-upload:${placementKey}`;
    setSaving(token, true);

    try {
      const imageUrl = await websiteMediaService.uploadWebsiteMediaImage(placementKey, file);
      setSingletonDrafts(prev => ({
        ...prev,
        [placementKey]: {
          ...prev[placementKey],
          image_url: imageUrl,
        },
      }));
      toast.success(t('websiteContent.image_uploaded'));
    } catch (error) {
      toast.error(
        `${t('websiteContent.image_upload_error')}: ${get_error_message(error, t('websiteContent.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
      event.target.value = '';
    }
  };

  const handleSaveSingleton = async (placementKey: WebsiteMediaPlacementKey) => {
    const token = `singleton-save:${placementKey}`;
    setSaving(token, true);

    try {
      const definition = WEBSITE_MEDIA_DEFINITIONS_BY_KEY[placementKey];
      const draft = singletonDrafts[placementKey];

      if (!is_allowed_website_media_image_url(draft.image_url)) {
        throw new Error(t('websiteContent.invalid_image_host'));
      }

      const entry = await websiteMediaService.updateWebsiteMediaEntry({
        placementKey,
        imageUrl: draft.image_url === definition.default_image_url ? null : draft.image_url,
        altTextFr: draft.alt_text_fr === definition.default_alt_text_fr ? null : draft.alt_text_fr,
        altTextEn: draft.alt_text_en === definition.default_alt_text_en ? null : draft.alt_text_en,
      });

      setSingletonEntriesByKey(prev => ({
        ...prev,
        [placementKey]: entry,
      }));
      toast.success(t('websiteContent.save_success'));
    } catch (error) {
      toast.error(
        `${t('websiteContent.save_error')}: ${get_error_message(error, t('websiteContent.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
    }
  };

  const handleClearSingleton = async (placementKey: WebsiteMediaPlacementKey) => {
    const token = `singleton-clear:${placementKey}`;
    setSaving(token, true);

    try {
      const definition = WEBSITE_MEDIA_DEFINITIONS_BY_KEY[placementKey];
      await websiteMediaService.clearWebsiteMediaEntry(placementKey);
      setSingletonEntriesByKey(prev => {
        const nextEntries = { ...prev };
        delete nextEntries[placementKey];
        return nextEntries;
      });
      setSingletonDrafts(prev => ({
        ...prev,
        [placementKey]: {
          image_url: definition.default_image_url,
          alt_text_fr: definition.default_alt_text_fr,
          alt_text_en: definition.default_alt_text_en,
        },
      }));
      toast.success(t('websiteContent.clear_success'));
    } catch (error) {
      toast.error(
        `${t('websiteContent.clear_error')}: ${get_error_message(error, t('websiteContent.unknown_error'))}`
      );
    } finally {
      setSaving(token, false);
    }
  };

  const openWebsiteImageLibrary = (placementKey: WebsiteMediaPlacementKey) => {
    setWebsiteImageLibraryTarget(placementKey);
    setWebsiteImageLibraryOpen(true);
  };

  const handleWebsiteImageLibraryPick = (url: string) => {
    setWebsiteImageLibraryOpen(false);
    const placementKey = websiteImageLibraryTarget;
    setWebsiteImageLibraryTarget(null);
    if (!placementKey) {
      return;
    }

    setSingletonDrafts(prev => ({
      ...prev,
      [placementKey]: {
        ...prev[placementKey],
        image_url: url,
      },
    }));

    toast.success(t('websiteContent.image_selected'));
  };

  const selectedGroup = groupedSingletonPlacements.find(group => group.page === selectedPage);

  return (
    <div className="flex flex-col gap-8 px-4 py-6 md:px-6 lg:px-10">
      <div className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-secondary">{t('websiteContent.title')}</h1>
        <p className="max-w-4xl text-base text-gray-500">{t('websiteContent.subtitle')}</p>
      </div>

      {loading ? (
        <div className="rounded-3xl border border-gray-200 bg-white px-6 py-10 text-sm text-gray-500 shadow-sm">
          {t('loading')}
        </div>
      ) : !selectedGroup ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {groupedSingletonPlacements.map(({ page, sections }) => {
            const definitions = sections.flatMap(([, pageDefinitions]) => pageDefinitions);
            const thumbnailDraft = definitions[0] ? singletonDrafts[definitions[0].key] : undefined;

            return (
              <button
                key={page}
                type="button"
                onClick={() => setSelectedPage(page)}
                className="group flex flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white text-left shadow-sm transition hover:shadow-md"
              >
                <div className="h-40 w-full overflow-hidden bg-gray-100">
                  {thumbnailDraft ? (
                    <img
                      src={resolve_website_asset_url(thumbnailDraft.image_url)}
                      alt=""
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-gray-400">
                      {t('websiteContent.no_image')}
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-1 p-5">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t(`websiteContent.pages.${page}`)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {t('websiteContent.photo_count', { count: definitions.length })}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <button
            type="button"
            onClick={() => setSelectedPage(null)}
            className="inline-flex w-fit items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('websiteContent.back_to_pages')}
          </button>

          <h2 className="text-2xl font-semibold text-gray-900">
            {t(`websiteContent.pages.${selectedGroup.page}`)}
          </h2>

          {selectedGroup.sections.map(([section, definitions]) => (
            <div
              key={`${selectedGroup.page}-${section}`}
              className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="mb-4 flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t(`websiteContent.sections.${selectedGroup.page}.${section}`)}
                </h3>
                <p className="text-sm text-gray-500">{t('websiteContent.shared_locale_hint')}</p>
              </div>

              <div className="flex flex-col gap-5">
                {definitions.map(definition => {
                  const placementKey = definition.key;
                  const draft = singletonDrafts[placementKey];
                  const hasOverride = Boolean(singletonEntriesByKey[placementKey]);
                  const isSaving =
                    Boolean(savingKeys[`singleton-save:${placementKey}`]) ||
                    Boolean(savingKeys[`singleton-upload:${placementKey}`]) ||
                    Boolean(savingKeys[`singleton-clear:${placementKey}`]);

                  return (
                    <article
                      key={placementKey}
                      className="rounded-3xl border border-gray-200 bg-gray-50/60 p-4"
                    >
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900">
                            {t(`websiteContent.placements.${placementKey}`)}
                          </h4>
                          <p className="text-xs text-gray-500">{placementKey}</p>
                        </div>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            hasOverride
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {hasOverride
                            ? t('websiteContent.override_active')
                            : t('websiteContent.using_default')}
                        </span>
                      </div>

                      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                          <img
                            src={resolve_website_asset_url(draft.image_url)}
                            alt={currentLanguage === 'fr' ? draft.alt_text_fr : draft.alt_text_en}
                            className="h-56 w-full object-cover"
                          />
                        </div>

                        <div className="grid gap-3">
                          <div className="flex flex-wrap gap-3">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
                              <ImagePlus className="h-4 w-4" />
                              {t('websiteContent.upload')}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={event =>
                                  void handleSingletonImageUpload(placementKey, event)
                                }
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => openWebsiteImageLibrary(placementKey)}
                              disabled={isSaving}
                              className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                            >
                              <Images className="h-4 w-4" />
                              {t('websiteContent.choose_from_library')}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleClearSingleton(placementKey)}
                              className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                              disabled={isSaving}
                            >
                              {t('websiteContent.clear')}
                            </button>
                          </div>

                          <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <span>{t('websiteContent.fields.image_url')}</span>
                            <input
                              type="url"
                              value={draft.image_url}
                              onChange={event =>
                                updateSingletonDraft(placementKey, 'image_url', event.target.value)
                              }
                              className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                            />
                          </label>

                          <div className="grid gap-3 md:grid-cols-2">
                            <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                              <span>{t('websiteContent.fields.alt_text_fr')}</span>
                              <textarea
                                value={draft.alt_text_fr}
                                onChange={event =>
                                  updateSingletonDraft(
                                    placementKey,
                                    'alt_text_fr',
                                    event.target.value
                                  )
                                }
                                rows={2}
                                className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                              <span>{t('websiteContent.fields.alt_text_en')}</span>
                              <textarea
                                value={draft.alt_text_en}
                                onChange={event =>
                                  updateSingletonDraft(
                                    placementKey,
                                    'alt_text_en',
                                    event.target.value
                                  )
                                }
                                rows={2}
                                className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm"
                              />
                            </label>
                          </div>

                          <div className="flex justify-end pt-1">
                            <button
                              type="button"
                              onClick={() => void handleSaveSingleton(placementKey)}
                              disabled={isSaving}
                              className="rounded-full bg-secondary px-5 py-2.5 text-sm font-semibold text-white hover:bg-secondary/90 disabled:opacity-50"
                            >
                              {isSaving ? t('websiteContent.saving') : t('websiteContent.save')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <WebsiteImageLibraryModal
        isOpen={websiteImageLibraryOpen}
        onClose={() => {
          setWebsiteImageLibraryOpen(false);
          setWebsiteImageLibraryTarget(null);
        }}
        onPick={handleWebsiteImageLibraryPick}
      />
    </div>
  );
}
